import classNames from 'classnames';
import { forwardRef, useCallback, useRef, useState } from 'react';
import isEqual from 'lodash/isEqual';

import type { Question, QuestionProperties } from '@api/instant_survey/types';
import { QUESTION_TYPES } from '@api/instant_survey';
import type { PopoverProps } from '@utilities/components/Popover';
import { Button, Checkbox, Fieldset, Layout, Overlay, Pill, Popover, Radio } from '@utilities';

import { FORMATS } from '../../../../utilities/constants';
import styles from './_index.module.scss';

interface AnswersFormatProps {
  question: Question;
  updateQuestionProperties: (properties: Partial<QuestionProperties>) => void;
}

const AnswersFormat = ({ question, updateQuestionProperties }: AnswersFormatProps) => {
  const pillRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLSpanElement>(null);
  const [showPopover, setShowPopover] = useState(false);

  const handleApply = useCallback(
    (propertiesToApply: Partial<QuestionProperties>) => {
      const isPercent = propertiesToApply[FORMATS.PERCENT.value];
      updateQuestionProperties({
        ...propertiesToApply,
        ...(isPercent && {
          maxNumber: 100,
          ...(question.type === QUESTION_TYPES.CONTSUM.value && { maxTotal: 100 }),
          minNumber: 0,
        }),
      });
      setShowPopover(false);
    },
    [question.type, updateQuestionProperties]
  );

  const pillValue = question.properties[FORMATS.PERCENT.value]
    ? FORMATS.PERCENT.name
    : question.properties[FORMATS.CURRENCY.value]
      ? FORMATS.CURRENCY.name
      : FORMATS.NUMBER.name;

  return (
    <Layout.Flex className={styles['answers-format']} gap="x-small">
      <span ref={popoverRef}>Format</span>
      <Pill buttonRef={pillRef} onClick={() => setShowPopover(!showPopover)} value={pillValue} />
      <Overlay
        container={popoverRef.current}
        onHide={() => setShowPopover(false)}
        placement="bottom"
        rootClose
        show={showPopover}
        target={pillRef.current}
      >
        <AnswersFormatPopover onApply={handleApply} question={question} />
      </Overlay>
    </Layout.Flex>
  );
};

interface AnswersFormatPopoverProps extends PopoverProps {
  onApply: (properties: Partial<QuestionProperties>) => void;
  question: Question;
}

const AnswersFormatPopover = forwardRef<HTMLDivElement, AnswersFormatPopoverProps>(
  ({ className, onApply, question, ...props }: AnswersFormatPopoverProps, ref) => {
    const [propertiesToApply, setPropertiesToApply] = useState({
      ...question.properties,
    });

    const isApplyDisabled = isEqual(question.properties, propertiesToApply);

    return (
      <Popover
        className={classNames(className, styles['answers-format-popover'])}
        id="answers-format-popover"
        ref={ref}
        {...props}
      >
        <Popover.Body>
          <Fieldset legend="Answer Format">
            <Layout.Flex.Column gap="small">
              {[FORMATS.NUMBER, FORMATS.CURRENCY, FORMATS.PERCENT].map((format) => (
                <Radio
                  isChecked={!!propertiesToApply[format.value]}
                  key={format.value}
                  label={format.name}
                  onChange={() => {
                    setPropertiesToApply((prev) => {
                      const resetFormats = {
                        ...prev,
                        [FORMATS.CURRENCY.value]: false,
                        [FORMATS.NUMBER.value]: false,
                        [FORMATS.PERCENT.value]: false,
                      };
                      return {
                        ...resetFormats,
                        [format.value]: true,
                      };
                    });
                  }}
                  value={format.value}
                />
              ))}
            </Layout.Flex.Column>
          </Fieldset>
          <hr className={styles['answers-format-popover-divider']} />
          <Fieldset legend="Additional Options">
            <Checkbox
              isChecked={propertiesToApply.onlyWholeNum}
              label="Force whole number"
              onChange={() =>
                setPropertiesToApply((prev) => ({
                  ...prev,
                  onlyWholeNum: !prev.onlyWholeNum,
                }))
              }
              value={propertiesToApply.onlyWholeNum ?? ''}
            />
          </Fieldset>
          <Button
            isDisabled={isApplyDisabled}
            onClick={() => onApply(propertiesToApply)}
            text="Apply"
          />
        </Popover.Body>
      </Popover>
    );
  }
);

AnswersFormatPopover.displayName = 'AnswersFormatPopover';

export default AnswersFormat;
